import { proStudioUrqlGql as urqlGql } from '@pypestream/api-services';
import { throwErrorWithId } from '@pypestream/utils';

import {
  checkForErrors,
  getPypeConnections,
  toSolutionEnvironment,
  type PlatformConnection,
} from '../../utils';
import { ProStudioContext, ProStudioEvents } from '../prostudio.xstate.types';

/**
 * Updates a Live Agent Platform connection.
 *
 * One mutation is enough: the backend's `updatePypeConnection` runs
 * `syncAgentRoutingForConnection`, re-applying the new routing/config to every
 * pype in the connection's `pypes` array. The previous implementation had to
 * snapshot the affected pypes itself by matching config values before the write
 * — that inference, and its "two connectors could be configured identically"
 * caveat, are both gone now that the binding is a real join row.
 *
 * `bot_retry` no longer needs preserving here either: this writes the connection,
 * not the pype, and the backend's bulk update only sends the agent-routing keys.
 */
export const updateLiveAgentPlatform = async (
  ctx: ProStudioContext,
  event: ProStudioEvents
): Promise<{
  pypeConnections: PlatformConnection[];
  cb?: () => void;
} | void> => {
  if (event.type !== 'updateLiveAgentPlatform') {
    return;
  }

  const projectId = ctx.project?.id;

  if (!projectId) {
    throwErrorWithId('Project ID is required');
    return;
  }

  const {
    id,
    name,
    platformId,
    agentRouting,
    agentRoutingConfig,
    agentRoutingUrl,
    env,
    cb,
  } = event.payload;

  const response = await urqlGql.updatePypeConnection({
    id,
    name,
    platformId,
    environment: toSolutionEnvironment(env),
    settings: agentRoutingConfig,
    agentRouting,
    // `null` is deliberate and must survive to the wire: the schema declares
    // `$agentRoutingUrl: String` (nullable) and the backend treats an explicit
    // `null` as "clear this field", while an OMITTED variable means "leave it
    // alone" — measured on main, 2026-09-04. Codegen is configured with
    // `InputMaybe<T> = T`, so its generated variable type says `string |
    // undefined` and cannot express that difference; the cast keeps the null
    // the payload builder decided on rather than collapsing it to `undefined`,
    // which would silently leave a stale callback URL on the connection.
    agentRoutingUrl: agentRoutingUrl as string | undefined,
  });
  checkForErrors(response);

  const pypeConnections = await getPypeConnections({ projectId });

  return { pypeConnections, cb };
};
